import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  PhxColumnGroupSegment,
  PhxPageChangeEvent,
  PhxSortChangeEvent,
  PhxSortDirection,
  PhxStickyColumnInfo,
  PhxTableColumnConfig,
  PhxTableColumnGroupConfig,
  PhxTableData,
  PhxTableHeaderConfig,
} from './phx-table.types';
import { PhxTableService } from './phx-table.service';
import { PhxTableRowComponent } from './row/phx-table-row.component';
import {
  buildColumnGroupData,
  buildStickyColumnsMap,
  CHECKBOX_COL_WIDTH,
  computeTableMinWidth,
  getColumnColWidth,
  normalizeHeaderConfigs,
  trackByColumnKey,
} from './phx-table.utils';
import { PhxDataSource } from '../services/data-source/phx-data-source.service';
import { PhxDateTimeFormatPipe } from '../pipes/phx-date-time-format.pipe';
import { PhxRelativeTimePipe } from '../pipes/phx-relative-time.pipe';
import { PHX_LOCALE_PROVIDER, PhxDateFormatStyle, PhxLocaleProvider } from '../locale/phx-locale.token';
import { Subject, Subscription } from 'rxjs';
import { skip, take, takeUntil } from 'rxjs/operators';
import { Inbox } from '../icon/icon-map';

@Component({
  selector: 'phx-table',
  templateUrl: './phx-table.component.html',
  styleUrls: ['./phx-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [PhxTableService, PhxDateTimeFormatPipe, PhxRelativeTimePipe],
  host: {
    class: 'phx-table',
    '[class.phx-table-clickable]': 'phxClickable',
    '[class.phx-table-selectable]': 'phxSelectable',
    '[class.phx-table-loading]': 'isLoading',
    '[class.phx-table-sticky-header]': 'phxStickyHeader',
    '[class.phx-table-has-group-rows]': 'hasGroupRows',
    '[style.max-height]': 'phxHeight ? phxHeight + "px" : null',
  },
})
export class PhxTableComponent<T extends PhxTableData = PhxTableData>
  implements OnChanges, OnDestroy, AfterContentInit, AfterViewInit
{
  // @Input() phxData: T[] = [];
  /** Row data synced from `phxDataSource.data$`. */
  public data: T[] = [];
  /** Column definitions controlling headers, visibility, width, and alignment. */
  @Input() phxColumnsConfig: PhxTableColumnConfig[] = [];
  /** Optional super-header groups rendered above the primary header row. */
  @Input() phxColumnGroups: PhxTableColumnGroupConfig[] = [];
  /** Default clickable state for rows (overridable per row). Default `true`. */
  @Input() phxClickable: boolean = true;
  /** When true, shows the loading skeleton overlay (ignored when `phxDataSource` is bound). */
  @Input() phxLoading: boolean = false;
  /** Max table height in pixels; enables vertical scrolling when set. */
  @Input() phxHeight: number | undefined;
  /** When true, renders a checkbox column and enables row selection. */
  @Input() phxSelectable: boolean = false;
  /** When true, keeps the header row visible while scrolling. Default `true`. */
  @Input() phxStickyHeader: boolean = true;
  /** Number of leading columns to pin horizontally; checkbox column is included when selectable. */
  @Input() phxStickyColumnsStart: number | undefined;
  /** Number of trailing columns to pin horizontally. */
  @Input() phxStickyColumnsEnd: number | undefined;
  /** Active sort column key; forwarded to header cells and updated when the user sorts. */
  @Input() phxSortKey: string | undefined;
  /** Sort direction for `phxSortKey`. `null` means unsorted. */
  @Input() phxSortDirection: PhxSortDirection = null;
  /** Total item count across all pages; when omitted, pagination still renders but hides the total-count label. */
  @Input() phxTotalItems: number | undefined;
  /** When true, hides pagination below the table. Default `false`. */
  @Input() phxHidePagination: boolean = false;
  /** Items per page for pagination. Default `25`. */
  @Input() phxPerPage: number = 25;
  /** Current 1-based page index for pagination. Default `1`. */
  @Input() phxCurrentPage: number = 1;
  /** Options shown in the rows-per-page selector. */
  @Input() phxPerPageOptions: number[] | undefined = [10, 25, 50, 100];
  /** Message shown when row data is empty. Defaults to a translated "No data available". */
  @Input() phxEmptyStateText?: string;
  /** Icon shown in the empty state. */
  @Input() phxEmptyStateIcon: string = Inbox;
  /** Data source for row data, loading state, and optional sort/pagination reloads. */
  @Input() phxDataSource!: PhxDataSource<T[]>;
  /** Whether the column customizer modal is open (two-way bindable). */
  @Input() phxColumnCustomizerOpen: boolean = false;
  /** When true, sort/page changes call the data source directly; when false, only events are emitted. */
  @Input() phxManageDataSource: boolean = false;

  /** Emitted when the set of selected rows changes. */
  @Output() phxSelectedRowsChange = new EventEmitter<T[]>();
  /** Emitted when the user clicks a sortable column header. */
  @Output() phxSortChange = new EventEmitter<PhxSortChangeEvent>();
  /** Emitted when the user changes page or rows-per-page. */
  @Output() phxPageChange = new EventEmitter<PhxPageChangeEvent>();
  /** Emitted when the column customizer open state changes (two-way bindable). */
  @Output() phxColumnCustomizerOpenChange = new EventEmitter<boolean>();
  /** Emitted when the user applies column visibility changes in the customizer. */
  @Output() phxColumnsConfigChange = new EventEmitter<PhxTableColumnConfig[]>();

  @ViewChild('scrollContainer', { static: false }) public scrollContainerRef: ElementRef<HTMLElement> | undefined;
  @ViewChild('tableElement', { static: false }) private _tableElementRef: ElementRef<HTMLElement> | undefined;
  @ViewChild('tableHead', { static: false }) private _tableHeadRef: ElementRef<HTMLElement> | undefined;

  /** Projected row components for accordion coordination. */
  @ContentChildren(PhxTableRowComponent, { descendants: true }) public rowComponents!: QueryList<
    PhxTableRowComponent<T>
  >;

  /** True when any projected row is a group header. */
  public hasGroupRows = false;

  /** Whether any visible column belongs to a configured group. */
  public hasColumnGroups: boolean = false;

  /** Computed segments for the group-header row. */
  public columnGroupSegments: PhxColumnGroupSegment[] = [];

  /** Set of column indices that are the last column in their group (for right border). */
  public groupBorderColumns: Set<number> = new Set();

  /** Columns filtered to only those that are visible (visible !== false). */
  public visibleColumns: PhxTableColumnConfig[] = [];

  /** Precomputed full-config index for each visible column. */
  public visibleColumnIndices: number[] = [];

  /** Cached skeleton row array — rebuilt only when phxPerPage changes. */
  public cachedSkeletonRows: number[] = new Array(25);

  /** Cached table min-width — rebuilt only when columns or selectable changes. */
  public cachedTableMinWidth: string | null = null;

  /** Precomputed header configs keyed by column key. */
  public headerConfigsCache: Map<string, PhxTableHeaderConfig[]> = new Map();

  /** Formatted absolute last-updated timestamp for pagination. */
  public lastUpdatedAtLabel = '';

  /** Formatted relative last-updated label for pagination. */
  public lastUpdatedAtRelativeLabel = '';

  public readonly trackByColumnKey = trackByColumnKey;
  /** Fixed width (px) for the auto-drawn checkbox column. */
  public readonly checkboxColWidth = CHECKBOX_COL_WIDTH;
  public readonly getColumnColWidth = getColumnColWidth;

  private static readonly _LAST_UPDATED_RELATIVE_REFRESH_MS = 15_000;

  // Managed manually — torn down on rebind / row changes.
  private _dataSourceSubs: Subscription[] = [];
  private _accordionSubs: Subscription[] = [];
  private _resizeObserver: ResizeObserver | undefined;
  private _scrollUnlisten: (() => void) | undefined;
  private _hasOverflow = false;
  private _headerHeight = 0;
  private _destroyed = false;
  /** Timestamp captured when row data last loaded; used to refresh the relative label. */
  private _lastUpdatedAt: Date | null = null;
  private _lastUpdatedRelativeIntervalId: ReturnType<typeof setInterval> | undefined;
  /** Guard so the default "open first group" runs only once. */
  private _groupsInitialized = false;
  private _destroy$ = new Subject<void>();
  /** Loading state from `phxDataSource`; kept separate from the `phxLoading` input. */
  private _dataSourceLoading = false;

  constructor(
    private _tableService: PhxTableService,
    private _cdr: ChangeDetectorRef,
    private _zone: NgZone,
    private _renderer: Renderer2,
    private _hostRef: ElementRef<HTMLElement>,
    private _dateTimeFormatPipe: PhxDateTimeFormatPipe,
    private _relativeTimePipe: PhxRelativeTimePipe,
    @Optional() @Inject(PHX_LOCALE_PROVIDER) private _phxLocale: PhxLocaleProvider | null,
  ) {
    this._tableService.selectedIds$.pipe(skip(1), takeUntil(this._destroy$)).subscribe(ids => {
      const selected = this.data.filter(d => ids.has(d.id));
      this.phxSelectedRowsChange.emit(selected);
      this._cdr.markForCheck();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const {
      phxColumnsConfig,
      phxColumnGroups,
      phxSelectable,
      phxClickable,
      phxStickyColumnsStart,
      phxStickyColumnsEnd,
      phxDataSource,
      phxPerPage,
      phxStickyHeader,
    } = changes;

    if (phxColumnsConfig) {
      this.visibleColumns = this.phxColumnsConfig.filter(c => c.visible !== false);
      this.visibleColumnIndices = this.visibleColumns.map(c => this.phxColumnsConfig.indexOf(c));
      this.headerConfigsCache = normalizeHeaderConfigs(this.visibleColumns);
      this._tableService.setColumnsConfig(this.phxColumnsConfig);
    }

    if (phxColumnsConfig || phxColumnGroups) {
      this.applyColumnGroupData();
    }

    if (phxColumnsConfig || phxSelectable) {
      this.cachedTableMinWidth = computeTableMinWidth(this.visibleColumns, this.phxSelectable);
    }

    if (phxSelectable) {
      this._tableService.selectable = this.phxSelectable;
    }

    if (phxClickable) {
      this._tableService.clickable = this.phxClickable;
    }

    if (phxStickyColumnsStart || phxStickyColumnsEnd || phxSelectable || phxColumnsConfig) {
      this._tableService.setStickyColumns(
        buildStickyColumnsMap(
          this.phxColumnsConfig,
          this.phxStickyColumnsStart,
          this.phxStickyColumnsEnd,
          this.phxSelectable,
        ),
      );
    }

    if (phxPerPage) {
      this.cachedSkeletonRows = new Array(Math.min(this.phxPerPage, 25));
    }

    if (phxDataSource) {
      this.bindDataSource();
    }

    if (phxStickyHeader && this._tableHeadRef) {
      this.syncOverflowMeasurements();
    }
  }

  public ngAfterContentInit(): void {
    this.refreshRowGroups();
    this.rowComponents.changes.pipe(takeUntil(this._destroy$)).subscribe(() => this.refreshRowGroups());
  }

  public ngAfterViewInit(): void {
    this.setupOverflowDetection();
  }

  public ngOnDestroy(): void {
    this._destroyed = true;
    this.teardownDataSource();
    this.teardownOverflowDetection();
    this._accordionSubs.forEach(s => s.unsubscribe());
    this._destroy$.next();
    this._destroy$.complete();
  }

  public get isLoading(): boolean {
    return this.phxLoading || this._dataSourceLoading;
  }

  public get selectableCount(): number {
    let count = 0;
    for (const d of this.data) {
      if (!this._tableService.disabledIds.has(d.id)) count++;
    }
    return count;
  }

  public get isAllSelected(): boolean {
    const count = this.selectableCount;
    return count > 0 && this._tableService.selectedIds.size === count;
  }

  public get isIndeterminate(): boolean {
    const size = this._tableService.selectedIds.size;
    return size > 0 && size < this.selectableCount;
  }

  public get totalColumns(): number {
    return this.visibleColumns.length + (this.phxSelectable ? 1 : 0);
  }

  /** Whether another page is available when the total item count is unknown. */
  public get paginationHasNextPage(): boolean | undefined {
    if (this.phxTotalItems != null) {
      return undefined;
    }

    if (this.phxManageDataSource && this.phxDataSource?.pagination) {
      return this.phxDataSource.hasNextPage;
    }

    return (this.data?.length ?? 0) >= this.phxPerPage;
  }

  public onSelectAllChange(checked: boolean): void {
    if (checked) {
      this._tableService.selectAll(this.data);
    } else {
      this._tableService.clearSelection();
    }
  }

  public getHeaderConfigs(column: PhxTableColumnConfig): PhxTableHeaderConfig[] {
    return this.headerConfigsCache.get(column.key) || [];
  }

  public getStickyInfo(colIndex: number): PhxStickyColumnInfo | undefined {
    return this._tableService.stickyColumns.get(colIndex);
  }

  public onHeaderSortChange(event: PhxSortChangeEvent): void {
    this.phxSortKey = event.key;
    this.phxSortDirection = event.direction;

    if (this.phxManageDataSource) {
      this.phxDataSource.setSort({ key: event.key, direction: event.direction });
    }

    this.phxSortChange.emit(event);
  }

  /** Handle pagination events; delegates to the data source when managed. */
  public onPageChange(event: PhxPageChangeEvent): void {
    this.phxCurrentPage = event.page;

    if (this.phxManageDataSource) {
      if (event.perPage !== this.phxPerPage) {
        this.phxPerPage = event.perPage;
        this.phxDataSource.setPerPage(event.perPage);
      } else {
        this.phxDataSource.setPage(event.page);
      }
    }

    this.phxPageChange.emit(event);
  }

  /** Whether a column index is the last in its group. */
  public isGroupBorderColumn(colIdx: number): boolean {
    return this.groupBorderColumns.has(colIdx);
  }

  public getFullIndex(visibleIdx: number): number {
    return this.visibleColumnIndices[visibleIdx];
  }

  public onColumnCustomizerOpenChange(open: boolean): void {
    this.phxColumnCustomizerOpen = open;
    this.phxColumnCustomizerOpenChange.emit(open);
  }

  public onCustomizerColumnsConfigChange(config: PhxTableColumnConfig[]): void {
    this.phxColumnsConfigChange.emit(config);
  }

  /** Recompute group-row state when projected rows change. */
  private refreshRowGroups(): void {
    const rows = this.rowComponents.toArray();
    this.hasGroupRows = rows.some(r => r.phxGroupRow);
    this.setupAccordion(rows);
    this._cdr.markForCheck();
  }

  /** Single-open accordion for group headers; opens the first group once when uncontrolled. */
  private setupAccordion(rows: PhxTableRowComponent<T>[]): void {
    this._accordionSubs.forEach(s => s.unsubscribe());
    this._accordionSubs = [];

    const groupRows = rows.filter(r => r.phxGroupRow);
    if (!groupRows.length) return;

    groupRows.forEach(row => {
      this._accordionSubs.push(
        row.phxGroupOpenChange.subscribe((open: boolean) => {
          if (!open) return;
          groupRows.forEach(other => {
            if (other !== row) other.setGroupOpen(false);
          });
        }),
      );
    });

    // Open the first group once on initial load; defer to avoid ExpressionChangedAfterItHasBeenChecked.
    if (!this._groupsInitialized) {
      this._groupsInitialized = true;
      queueMicrotask(() => {
        if (this._destroyed) return;
        const current = this.rowComponents.filter(r => r.phxGroupRow);
        if (current.length && !current.some(r => r.open)) {
          current[0].setGroupOpen(true);
        }
      });
    }
  }

  private bindDataSource(): void {
    this.teardownDataSource();
    if (!this.phxDataSource) {
      this._cdr.markForCheck();
      return;
    }

    const ds = this.phxDataSource;

    this.data = ds.data ?? [];
    this._dataSourceLoading = ds.loading ?? false;
    this.syncPaginationFromDataSource();

    this._dataSourceSubs.push(
      ds.data$.subscribe(data => {
        this.data = data ?? [];
        this.updateLastUpdatedLabels(new Date());
        this.syncPaginationFromDataSource();
        // Selections are page-scoped; clear on each data reload (sort/page change).
        this._tableService.clearSelection();
        this._cdr.markForCheck();
      }),
      ds.loading$.subscribe(loading => {
        this._dataSourceLoading = loading;
        this._cdr.markForCheck();
      }),
    );

    this._cdr.markForCheck();
  }

  private teardownDataSource(): void {
    this.clearLastUpdatedRelativeInterval();
    this._dataSourceSubs.forEach(s => s.unsubscribe());
    this._dataSourceSubs = [];
    this._dataSourceLoading = false;
  }

  /** Watch the scroll container for horizontal overflow via ResizeObserver + scroll. */
  private setupOverflowDetection(): void {
    const el = this.scrollContainerRef?.nativeElement;
    if (!el) return;

    const check = () => this.syncOverflowMeasurements(el);

    this._zone.runOutsideAngular(() => {
      this._resizeObserver = new ResizeObserver(check);
      this._resizeObserver.observe(el);

      const table = this._tableElementRef?.nativeElement;
      if (table) this._resizeObserver.observe(table);

      this._scrollUnlisten = this._renderer.listen(el, 'scroll', check);
      check();
    });
  }

  /** Apply overflow class and header-height CSS var via Renderer2 (no change detection). */
  private syncOverflowMeasurements(scrollEl?: HTMLElement): void {
    const el = scrollEl ?? this.scrollContainerRef?.nativeElement;
    const host = this._hostRef.nativeElement;
    if (!el) return;

    const overflows = el.scrollWidth > el.clientWidth;
    if (overflows !== this._hasOverflow) {
      this._hasOverflow = overflows;
      if (overflows) {
        this._renderer.addClass(host, 'phx-table-has-overflow');
      } else {
        this._renderer.removeClass(host, 'phx-table-has-overflow');
      }
    }

    const head = this._tableHeadRef?.nativeElement;
    const height = this.phxStickyHeader && head ? head.offsetHeight : 0;
    if (height !== this._headerHeight) {
      this._headerHeight = height;
      this._renderer.setStyle(host, '--phx-table-header-height', `${height}px`);
    }
  }

  private teardownOverflowDetection(): void {
    this._resizeObserver?.disconnect();
    this._resizeObserver = undefined;
    this._scrollUnlisten?.();
    this._scrollUnlisten = undefined;
  }

  /** Skips sync in controlled mode so consumer-owned pagination inputs are not overwritten. */
  private syncPaginationFromDataSource(): void {
    if (!this.phxManageDataSource) return;
    if (!this.phxDataSource.pagination) return;
    const p = this.phxDataSource.pagination;
    this.phxTotalItems = p.totalItems;
    this.phxCurrentPage = p.pageNo;
    this.phxPerPage = p.perPage;
  }

  private applyColumnGroupData(): void {
    const result = buildColumnGroupData(this.visibleColumns, this.visibleColumnIndices, this.phxColumnGroups);
    this.hasColumnGroups = result.hasGroups;
    this.columnGroupSegments = result.segments;
    this.groupBorderColumns = result.rightBorders;
    this._tableService.setGroupBorderColumns(result.rightBorders);
  }

  private updateLastUpdatedLabels(date: Date): void {
    this._lastUpdatedAt = date;

    if (this._phxLocale) {
      this.lastUpdatedAtLabel = this._phxLocale.formatDate(date, PhxDateFormatStyle.WITH_TIME_ZONE);
    } else {
      this.lastUpdatedAtLabel = this.formatLastUpdatedAtFallback(date);
    }

    this.updateLastUpdatedRelativeLabel();
    this.startLastUpdatedRelativeInterval();
  }

  private updateLastUpdatedRelativeLabel(): void {
    if (!this._lastUpdatedAt) {
      this.lastUpdatedAtRelativeLabel = '';
      return;
    }

    const date = this._lastUpdatedAt;

    if (this._phxLocale) {
      this.lastUpdatedAtRelativeLabel = this._phxLocale.formatElapsed(date, { singleUnit: true });
      return;
    }

    this._relativeTimePipe
      .transform(date.toISOString(), 'compact', '', true, false)
      ?.pipe(take(1), takeUntil(this._destroy$))
      .subscribe(label => {
        this.lastUpdatedAtRelativeLabel = label;
        this._cdr.markForCheck();
      });
  }

  private startLastUpdatedRelativeInterval(): void {
    this.clearLastUpdatedRelativeInterval();
    if (!this._lastUpdatedAt) return;

    this._lastUpdatedRelativeIntervalId = setInterval(() => {
      if (this._destroyed) return;
      this.updateLastUpdatedRelativeLabel();
      if (this._phxLocale) {
        this._cdr.markForCheck();
      }
    }, PhxTableComponent._LAST_UPDATED_RELATIVE_REFRESH_MS);
  }

  private clearLastUpdatedRelativeInterval(): void {
    if (this._lastUpdatedRelativeIntervalId !== undefined) {
      clearInterval(this._lastUpdatedRelativeIntervalId);
      this._lastUpdatedRelativeIntervalId = undefined;
    }
  }

  private formatLastUpdatedAtFallback(date: Date): string {
    const formatted = this._dateTimeFormatPipe.transform(date, 'compact', 'date-time');
    const timezoneAbbrev = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(date)
      .find(part => part.type === 'timeZoneName')?.value;

    return timezoneAbbrev ? `${formatted} ${timezoneAbbrev}` : formatted;
  }
}
